import { useEffect, useState } from 'react';
import type { IntegrationConnection } from '../utils/integrations-client/jira/connections';
import getProjectsAndIssueTypes, {
  type ProjectsAndIssueTypes
} from '../utils/integrations-client/jira/getProjectsAndIssueTypes';
import type { ErrorOrNull } from '../../../typings/utils';
import { useAuthContext } from '../contexts/auth';
import { useEnterprises } from '../contexts/enterprises';
import { type IntegrationTemplate } from '../api-client';

type UseIntegrationProjectsFromTemplates = {
  connections: IntegrationConnection[];
  templates: IntegrationTemplate[];
};

export type ProjectsByConnections = Record<string, ProjectsAndIssueTypes[]>;

const useIntegrationProjectsFromTemplates = ({
  connections,
  templates
}: UseIntegrationProjectsFromTemplates) => {
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [projectsByConnections, setProjectsByConnections] =
    useState<ProjectsByConnections>({});
  const [projectsError, setProjectsError] = useState<ErrorOrNull>(null);

  const { user } = useAuthContext();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const token = user!.token;
  const { activeEnterprise } = useEnterprises();

  useEffect(() => {
    if (!connections.length || !activeEnterprise || !templates.length) {
      return;
    }

    const templatesByConnections = templates.reduce<
      Record<string, IntegrationTemplate[]>
    >((filteredObject, template) => {
      const templatesForConnection =
        filteredObject[template.integration_connection_id] || [];

      filteredObject[template.integration_connection_id] = [
        ...templatesForConnection,
        template
      ];

      return filteredObject;
    }, {});

    const fetchProjects = async () => {
      setIsProjectsLoading(true);

      try {
        const projectsConnectionsMap: Record<string, ProjectsAndIssueTypes[]> =
          {};

        for (const { id } of connections) {
          const currentConnectionTemplate = templatesByConnections[id];

          const usedProjectsFromTemplatesSet = new Set(
            currentConnectionTemplate?.map(temp => temp.project_id)
          );

          if (
            !currentConnectionTemplate?.length ||
            !usedProjectsFromTemplatesSet.size
          ) {
            continue;
          }

          const connectionProjects = await getProjectsAndIssueTypes({
            enterpriseId: activeEnterprise.id,
            connectionId: id,
            token
          });

          const templatesProjects = connectionProjects?.filter(pr =>
            usedProjectsFromTemplatesSet.has(pr.id)
          );

          projectsConnectionsMap[id] = templatesProjects;
        }

        setProjectsByConnections(projectsConnectionsMap);
      } catch (error) {
        setProjectsError(error as Error);
      } finally {
        setIsProjectsLoading(false);
      }
    };

    fetchProjects();
  }, [connections, activeEnterprise, templates]);

  return {
    projectsByConnections,
    isProjectsLoading,
    projectsError
  };
};

export default useIntegrationProjectsFromTemplates;
